'use client';

import { Divider } from '@common/components';
import {
  SvgChevronleft,
  SvgFilter,
  SvgMagnifyingGlass,
  SvgTextfieldclose,
} from '@common/icon';
import { cn } from '@core/utils';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { useCustomRouter } from '@/hooks/useCustomRouter';

import { useHideOnScroll } from './_hooks/useHideOnScroll';
import { SearchCategoryDropdown } from './SearchCategoryDropdown';

interface SearchBarProps {
  keyword?: string;
  filterCount?: number;
  shouldAutoFocus?: boolean;
  /** 모바일에서 sticky + 스크롤시 자동 숨김 적용 여부. overlay 안에서는 false. */
  sticky?: boolean;
  onSearch: (keyword: string) => void;
  onFilterClick?: () => void;
  /** input의 focus/blur 신호를 부모에 전파한다. 미지정 시 동작 없음. */
  onFocusChange?: (focused: boolean) => void;
}

export function SearchBar({
  keyword = '',
  filterCount = 0,
  shouldAutoFocus = false,
  sticky = true,
  onSearch,
  onFilterClick,
  onFocusChange,
}: SearchBarProps) {
  const router = useCustomRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(keyword);
  const { hidden } = useHideOnScroll({ enabled: sticky });

  useEffect(() => {
    setValue(keyword);
  }, [keyword]);

  useEffect(() => {
    if (shouldAutoFocus) inputRef.current?.focus();
  }, [shouldAutoFocus]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center',
        sticky && [
          'mobile-tablet:sticky mobile-tablet:top-0 mobile-tablet:z-10 mobile-tablet:bg-Gray-white',
          'mobile-tablet:transition-transform mobile-tablet:duration-200',
          hidden && 'mobile-tablet:-translate-y-full',
        ],
      )}
    >
      <div className="flex w-full max-w-[850px] items-center mobile-tablet:max-w-none mobile-tablet:gap-10 mobile-tablet:overflow-hidden mobile-tablet:px-20 mobile-tablet:py-4">
        <button
          type="button"
          aria-label="뒤로 가기"
          className="hidden shrink-0 mobile-tablet:flex"
          onClick={() => router.back()}
        >
          <SvgChevronleft className="size-24 text-Gray-900" />
        </button>

        <form
          role="search"
          onSubmit={handleSubmit}
          className="flex h-48 flex-1 items-center gap-12 rounded-pill border border-Gray-300 py-12 pl-16 pr-4 mobile-tablet:h-40 mobile-tablet:min-w-0 mobile-tablet:gap-12 mobile-tablet:text-clip mobile-tablet:rounded-xsmall mobile-tablet:border-0 mobile-tablet:bg-Gray-200 mobile-tablet:p-8"
        >
          <div className="flex shrink-0 items-center mobile-tablet:min-w-0 mobile-tablet:max-w-100 mobile-tablet:shrink mobile-tablet:overflow-hidden">
            <SearchCategoryDropdown />
            <Divider variant="vertical" className="h-20 shrink-0 bg-Gray-400" />
          </div>

          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onFocus={() => onFocusChange?.(true)}
            onBlur={() => onFocusChange?.(false)}
            placeholder="검색어를 입력하세요"
            className="flex-1 bg-transparent text-Gray-900 outline-none typo-body3 placeholder:text-Gray-550 mobile-tablet:min-w-0 mobile-tablet:tracking-[-0.28px] mobile-tablet:typo-body4"
          />

          {value.length > 0 && (
            <button
              type="button"
              aria-label="지우기"
              className="shrink-0 p-4 mobile-tablet:hidden"
              onClick={handleClear}
            >
              <SvgTextfieldclose className="size-20" originalFill />
            </button>
          )}

          <button
            type="submit"
            aria-label="검색"
            className="h-40 w-56 shrink-0 rounded-pill bg-Gray-black flex-center mobile-tablet:hidden"
          >
            <SvgMagnifyingGlass className="size-20 text-Gray-white" />
          </button>
        </form>

        {onFilterClick ? (
          <button
            type="button"
            aria-label="필터"
            className="hidden shrink-0 items-center gap-4 mobile-tablet:flex"
            onClick={onFilterClick}
          >
            <SvgFilter className="size-20 text-Gray-900" />
            {filterCount > 0 && (
              <span className="flex size-16 items-center justify-center rounded-pill bg-Gray-black font-700 text-Gray-white typo-caption2">
                {filterCount}
              </span>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}
